import * as React from 'react';
import {ReactElement} from 'react';
import * as autoBind from 'react-autobind';
import {BasicModal, AlertModal, ControlButton, TscButtonType} from '@techsmith/tsc-cloud-style-guide';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import ManageReviewersContainer from '../ManageReviewers/ManageReviewersContainer';
import ReviewService from '../../services/ReviewService/ReviewService';
import UserMessaging from '../../utils/UserMessaging';
import Strings from '../../globals/Strings';
import SimpleReviewerModel from '../../types/SimpleReviewerModel';
import Themeable from '../../types/Themeable';
import ReviewerRoleRequestModel from '../../types/ReviewerRoleRequestModel';
import Constants from '../../globals/Constants';
import GoogleAnalyticsService from '../../services/GoogleAnalyticsService/GoogleAnalyticsService';
import Store from '../../redux/Store';

export default class ManageReviewersModal extends React.Component<ManageReviewersModalProps, ManageReviewersModalState> {
   readonly state: ManageReviewersModalState = {
      isSubmitting: false,
      newReviewers: [],
      notifyReviewers: [],
      delReviewers: [],
      warnAboutCancel: false
   };

   constructor(props: ManageReviewersModalProps) {
      super(props);
      autoBind(this);
   }

   componentDidMount(): void {
      GoogleAnalyticsService.trackShareReview(Store.getState().currentUserId, Constants.googleAnalytics.shareReview.actions.openManageReviewModal);
   }

   removeNewReviewersWithEmails(emails: string[]): void {
      const {newReviewers, notifyReviewers} = this.state;
      this.setState({newReviewers: newReviewers.filter(newReviewer => !emails.includes(newReviewer.Email)), notifyReviewers: notifyReviewers.filter(reviewerToNotify => !emails.includes(reviewerToNotify.Email))});
   }

   removeDelReviewersWithEmails(emails: string[]): void {
      this.setState({delReviewers: this.state.delReviewers.filter(delReviewer => !emails.includes(delReviewer.Email))});
   }

   async saveReviewers(): Promise<void> {
      const {reviewId, onClose} = this.props;
      const {delReviewers, newReviewers} = this.state;
      this.setState({isSubmitting: true});

      const reviewerRoleRequest: ReviewerRoleRequestModel = {
         ReviewerEmailAddressesToAdd: newReviewers.map(r => r.Email),
         ReviewerEmailAddressesToNotify: this.getCombinedReviewers().filter(reviewer => reviewer.Notify).map(r => r.Email),
         ReviewerIdsToRemove: delReviewers.map(r => r.Id)
      };

      try {
         await ReviewService.updateReviewers(reviewId, reviewerRoleRequest);
         GoogleAnalyticsService.trackShareReview(Store.getState().currentUserId, Constants.googleAnalytics.shareReview.actions.editReviewers);
         if (delReviewers.length === 0) {
            this.setState({isSubmitting: false});
            onClose();
            return Promise.resolve();
         }
         await this.reloadReviewersAndComments().catch(() => {
            UserMessaging.warn(Strings.errors.failedUpdateReview);
         });
         this.setState({isSubmitting: false});
         onClose();
      } catch {
         await this.reloadReviewersAndComments().catch(() => { /* Attempt to update, but don't hang on it */});
         this.setState({isSubmitting: false});
         UserMessaging.warn(Strings.errors.failedUpdatingReviewers);
      }
   }

   async reloadReviewersAndComments(): Promise<void> {
      const {reviewId, versionNumber} = this.props;
      const version = await ReviewService.loadReview(reviewId, versionNumber);
      await ReviewService.loadComments(version);
   }

   addReviewers(reviewersToAdd: SimpleReviewerModel[]): void {
      const {delReviewers, newReviewers} = this.state;
      const {reviewers} = this.props;
      const existingReviewers = reviewers.concat(delReviewers).concat(newReviewers);
      const uniqueReviewersToAdd = reviewersToAdd.filter(reviewerToAdd => !existingReviewers.some(existingReviewer => reviewerToAdd.Email === existingReviewer.Email));
      this.removeDelReviewersWithEmails(reviewersToAdd.map(r => r.Email));
      this.setState({newReviewers: this.state.newReviewers.concat(uniqueReviewersToAdd), notifyReviewers: this.state.notifyReviewers.concat(uniqueReviewersToAdd)});
   }

   removeReviewer(reviewer: SimpleReviewerModel): void {
      const {newReviewers, delReviewers, notifyReviewers} = this.state;

      if (newReviewers.some(newR => newR.Email === reviewer.Email)) {
         this.removeNewReviewersWithEmails([reviewer.Email]);
         return;
      }

      if (delReviewers.some(delR => delR.Id === reviewer.Id)) {
         return;
      }

      if (notifyReviewers.some(r => r.Email === reviewer.Email)) {
         this.setReviewerNotify(reviewer, !reviewer.Notify);
      }

      this.setState({delReviewers: delReviewers.concat(Object.assign({}, reviewer, {Status: Constants.reviewerStatus.removing, Notify: false}))});
   }

   setReviewerNotify(reviewer: SimpleReviewerModel, wantToNotify: boolean): void {
      const {notifyReviewers} = this.state;

      if (wantToNotify && !notifyReviewers.some(r => r.Email === reviewer.Email)) {
         this.setState({notifyReviewers: notifyReviewers.concat(Object.assign({}, reviewer, {Notify: true}))});
      } else if (!wantToNotify && notifyReviewers.some(r => r.Email === reviewer.Email)) {
         this.setState({notifyReviewers: notifyReviewers.filter(r => r.Email !== reviewer.Email)});
      }
   }

   cancelRemoveReviewer(reviewer: SimpleReviewerModel): void {
      const reviewersToKeep = this.state.delReviewers.filter(delR => delR.Id !== reviewer.Id);
      this.setState({delReviewers: reviewersToKeep});
   }

   onCancel(): void {
      if (this.state.newReviewers.length > 0 || this.state.delReviewers.length > 0) {
         this.setState({warnAboutCancel: true});
      } else {
         this.props.onClose();
      }
   }

   onCancelWarnClose(): void {
      this.setState({warnAboutCancel: false});
   }

   onCancelWarnConfirm(): void {
      this.setState({warnAboutCancel: false});
      this.props.onClose();
   }

   getCombinedReviewers(): SimpleReviewerModel[] {
      const {delReviewers, newReviewers, notifyReviewers} = this.state;
      const {reviewers} = this.props;

      const combinedReviewers = reviewers.filter(r => !delReviewers.some(delR => delR.Id === r.Id)).concat(newReviewers).concat(delReviewers);
      combinedReviewers.forEach(cr => cr.Notify = notifyReviewers.some(nr => nr.Email === cr.Email));

      return combinedReviewers;
   }

   getPrimaryButtonText(pendingChanges: boolean): string {
      const {notifyReviewers} = this.state;

      if (pendingChanges && notifyReviewers.length === 0) {
         return Strings.labels.manageReviewersModal.save;
      } else if (pendingChanges && notifyReviewers.length > 0) {
         return Strings.labels.manageReviewersModal.saveAndNotify;
      } else {
         return Strings.labels.manageReviewersModal.done;
      }
   }

   render(): ReactElement<any> {
      const {isOwner, themeName, versionNumber} = this.props;
      const {isSubmitting, newReviewers, delReviewers, notifyReviewers} = this.state;
      const manageReviewersModalStrings = Strings.labels.manageReviewersModal;
      const pendingChanges = newReviewers.length > 0 || delReviewers.length > 0 || notifyReviewers.length > 0;
      const titleText = isOwner ? manageReviewersModalStrings.bannerTextOwner : manageReviewersModalStrings.bannerTextReviewer;
      const primaryButtonText = this.getPrimaryButtonText(pendingChanges);
      const primaryButtonHandler = pendingChanges ? this.saveReviewers : this.onCancel;

      return (
         <BasicModal themeName={themeName} title={titleText} onClose={this.onCancel}>
            <div className="modal-body">
               <div className="review-modal-body invite-modal-container">
                  {isOwner && isSubmitting && <LoadingSpinner/>}
                  <ManageReviewersContainer showInput={isOwner}
                                            isOwner={isOwner}
                                            reviewers={this.getCombinedReviewers()}
                                            showRemoveReviewerText={!!delReviewers.length && versionNumber > 1}
                                            addReviewers={this.addReviewers}
                                            cancelRemoveReviewer={this.cancelRemoveReviewer}
                                            removeReviewer={this.removeReviewer}
                                            enableSaveGroup={true}
                                            notifyReviewer={this.setReviewerNotify}/>
                  <div className="button-group">
                     <ControlButton themeName={themeName}
                                    disabled={isSubmitting}
                                    buttonType={TscButtonType.secondary}
                                    isFullWidth={true}
                                    onClick={this.onCancel}
                                    label={manageReviewersModalStrings.cancel}
                                    testId="manage-reviewers-modal-cancel-button"
                                    visible={pendingChanges}/>
                     <ControlButton themeName={themeName}
                                    disabled={isSubmitting}
                                    buttonType={TscButtonType.primary}
                                    isFullWidth={true}
                                    onClick={primaryButtonHandler}
                                    label={primaryButtonText}
                                    testId="manage-reviewers-modal-primary-button"/>
                  </div>
               </div>
               {this.state.warnAboutCancel && <AlertModal title={manageReviewersModalStrings.alertTitle}
                                                          testId="manage-reviewers-warn"
                                                          onClose={this.onCancelWarnClose}
                                                          closeContent={manageReviewersModalStrings.alertClose}
                                                          onConfirm={this.onCancelWarnConfirm}
                                                          confirmContent={manageReviewersModalStrings.alertConfirm}
                                                          confirmButtonType={TscButtonType.destructive}
                                                          onDismiss={this.onCancelWarnClose}
                                                          dismissContent={manageReviewersModalStrings.alertClose}
                                                          modalWidth="20rem">
                  <div className="manage-reviewers-warn">{manageReviewersModalStrings.alertText}</div>
               </AlertModal>}
            </div>
         </BasicModal>
      );
   }
}

export interface ManageReviewersModalProps extends Themeable {
   reviewId: string;
   title: string;
   reviewers: SimpleReviewerModel[];
   isOwner: boolean;
   versionNumber: number;
   onClose(): void;
}

export interface ManageReviewersModalState {
   isSubmitting: boolean;
   newReviewers: SimpleReviewerModel[];
   delReviewers: SimpleReviewerModel[];
   notifyReviewers: SimpleReviewerModel[];
   warnAboutCancel: boolean;
}
